import type { ProcessJobType } from '@/graphql/graphql';
import {
  flattenJobParams,
  jobParamValueText,
  jobParamLabel,
  type JobParamEntry,
  type JobParamTranslate,
} from '@/lib/jobParams';
import { cn } from '@/lib/utils';
import { Check, Copy } from 'lucide-react';
import { FunctionComponent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The labelled parameter list of a process job's `inputJson` — the
 * presentational piece shared by the data centre's staff-only `JobParams` and
 * the Analyses run-details drawer.
 *
 * `staffTools` adds the internals (S3 keys, ids), the raw JSON and Copy; the
 * caller decides who counts as staff. `collapseAfter` shows only the first N
 * parameters behind a "Show all (n)" toggle. With nothing to list it renders
 * `emptyText` (or nothing).
 */
export const JobParamsPanel: FunctionComponent<{
  inputJson: unknown;
  type: ProcessJobType | undefined;
  staffTools: boolean;
  collapseAfter?: number;
  emptyText?: string;
}> = ({ inputJson, type, staffTools, collapseAfter, emptyText }) => {
  // The panel's keys are assembled at runtime (`groundClassification.runElm`,
  // `batch.srid`), so they can't satisfy `TFunction`'s literal-key union.
  const t = useTranslation('project').t as JobParamTranslate;
  const [showInternals, setShowInternals] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [copied, setCopied] = useState(false);

  const { params, internals } = useMemo(() => flattenJobParams(inputJson), [inputJson]);

  if (params.length === 0 && (!staffTools || internals.length === 0)) {
    return emptyText ? <p className='text-xs text-slate-500 dark:text-stone-400'>{emptyText}</p> : null;
  }

  const collapsed = collapseAfter !== undefined && !showAll && params.length > collapseAfter;
  const visibleParams = collapsed ? params.slice(0, collapseAfter) : params;

  const handleCopy = () => {
    navigator.clipboard?.writeText(JSON.stringify(inputJson, null, 2)).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => {},
    );
  };

  return (
    <div className='rounded-md border border-slate-200/70 bg-slate-50/60 px-3 py-2 dark:border-stone-700/70 dark:bg-stone-900/40'>
      <dl className='grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2'>
        {visibleParams.map((entry) => (
          <ParamRow key={entry.path} entry={entry} type={type} t={t} />
        ))}
        {staffTools &&
          showInternals &&
          internals.map((entry) => <ParamRow key={entry.path} entry={entry} type={type} t={t} />)}
      </dl>

      {collapseAfter !== undefined && params.length > collapseAfter && (
        <button
          type='button'
          data-testid='job-params-show-all'
          onClick={() => setShowAll((v) => !v)}
          className='mt-1 text-xs text-slate-500 underline-offset-2 hover:underline dark:text-stone-400'
        >
          {showAll ? t('batch.showFewerParameters') : `${t('batch.showAllParameters')} (${params.length})`}
        </button>
      )}

      {staffTools && (
        <div className='mt-2 flex flex-wrap items-center gap-3 border-t border-slate-200/70 pt-2 text-xs dark:border-stone-700/70'>
          {internals.length > 0 && (
            <button
              type='button'
              data-testid='job-internals-toggle'
              onClick={() => setShowInternals((v) => !v)}
              className='text-slate-500 underline-offset-2 hover:underline dark:text-stone-400'
            >
              {showInternals ? t('batch.hideInternals') : t('batch.showInternals')}{' '}
              <span className='opacity-60'>({internals.length})</span>
            </button>
          )}
          <button
            type='button'
            data-testid='job-raw-toggle'
            onClick={() => setShowRaw((v) => !v)}
            className='text-slate-500 underline-offset-2 hover:underline dark:text-stone-400'
          >
            {showRaw ? t('batch.hideRawJson') : t('batch.showRawJson')}
          </button>
          <button
            type='button'
            onClick={handleCopy}
            className='ml-auto flex items-center gap-1 text-slate-500 underline-offset-2 hover:underline dark:text-stone-400'
          >
            {copied ? <Check className='size-3.5 text-emerald-500' /> : <Copy className='size-3.5' />}
            {copied ? t('batch.copiedJson') : t('batch.copyJson')}
          </button>
        </div>
      )}

      {staffTools && showRaw && (
        <pre
          data-testid='job-raw-json'
          className='mt-2 max-h-80 overflow-auto rounded bg-slate-900 p-2 text-[11px] leading-relaxed text-slate-100 dark:bg-black/50'
        >
          {JSON.stringify(inputJson, null, 2)}
        </pre>
      )}
    </div>
  );
};

const ParamRow: FunctionComponent<{
  entry: JobParamEntry;
  type: ProcessJobType | undefined;
  t: JobParamTranslate;
}> = ({ entry, type, t }) => (
  <div data-testid='job-param-row' className='flex items-baseline justify-between gap-3 text-xs'>
    <dt className='min-w-0 truncate text-slate-500 dark:text-stone-400' title={entry.path}>
      {jobParamLabel(t, type ?? ('POST' as ProcessJobType), entry)}
    </dt>
    <dd
      title={jobParamValueText(t, type, entry)}
      className={cn(
        'min-w-0 truncate text-right font-medium tabular-nums text-slate-900 dark:text-stone-100',
        entry.value === true && 'text-emerald-600 dark:text-emerald-400',
        entry.value === false && 'text-slate-400 dark:text-stone-500',
      )}
    >
      {jobParamValueText(t, type, entry)}
    </dd>
  </div>
);
